/* eslint-disable react/no-multi-comp */
import React from 'react';
import PropTypes from 'prop-types';
import PaginationModule from './PaginationModule';
import CardsWrapper from './components/CardsWrapper';
import CardsLabel from './components/CardsLabel';
import CardsList from './components/CardsList';
import NarrowCardPlaceholder from './components/NarrowCardPlaceholder';
import PresentationCard from '../../PresentationCard';
import ProgressSpinner from '../../../material/ProgressSpinner';
import useContainerProps from '../../../hooks/useContainerProps';

/** renders a cards module intended for use inside a section */
const CardsModule = props => {
  props = useContainerProps(props);
  const { background, border, corner, shadow, mediaBackground, children, collection = [],
    keyField, label, labelPosition, type, isLoadingData,
    postCards, preCards } = props;

  const styleConfig = { background, corner, shadow, border, mediaBackground };

  const wrapWithCard = (member, content) => {
    return (
      <PresentationCard isWrapper={true} key={member[keyField]} {...props} type={type} styleConfig={styleConfig} >
        {content}
      </PresentationCard>
    );
  }

  const renderCards = () => {
    const cards = [];
    cards.push(...renderFixedCards(preCards, '__preCards__'));
    if (typeof children === 'function') {
      cards.push(...collection.map((member, index) => {
        const child = children(member, index);
        if (child && child.type === PresentationCard) {
          return React.cloneElement(child, { styleConfig })
        } else {
          return wrapWithCard(member, child);
        }
      }));
    } else {
      cards.push(...collection.map(member => React.Children.map(children, child => {
        return wrapWithCard(member, React.cloneElement(child, { member }));
      })));
    }
    cards.push(...renderFixedCards(postCards, '__postCards__'));
    return cards;
  }

  const renderFixedCards = (elements, keyPrefix) => {
    if (!elements) {
      return [];
    }
    if (!Array.isArray(elements)) {
      elements = [elements];
    }
    return elements.map((element, index) => wrapWithCard({ [keyField]: `${keyPrefix}${index}` }, element));
  }

  const renderPlaceholders = () => {
    if (type !== 'narrow') {
      return null;
    }
    // render three placeholders to make sure narrow cards stay left aligned
    return [0, 1, 2].map(index => <NarrowCardPlaceholder key={index} />);
  }

  return (
    <CardsWrapper type={type}>
      {isLoadingData && <ProgressSpinner containerClassName="cards-module-loader"/>}
      {label && <CardsLabel labelPosition={labelPosition}>{label}</CardsLabel>}
      <PaginationModule {...props}>
        <CardsList className={isLoadingData ? 'is-loading' : undefined}>
          {renderCards()}
          {renderPlaceholders()}
        </CardsList>
      </PaginationModule>
    </CardsWrapper>
  );
};

CardsModule.propTypes = {
  background: PropTypes.oneOf(['white']),
  border: PropTypes.oneOf(['all', 'vertical', 'horizontal']),
  children: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.node
  ]),
  collection: PropTypes.array,
  corner: PropTypes.oneOf(['none', 'small']),
  isBranded: PropTypes.bool,
  isLoadingData: PropTypes.bool,
  /** collection prop used as the key for each list item */
  keyField: PropTypes.string,
  /** label to display above the cards list */
  label: PropTypes.string,
  labelPosition: PropTypes.string,
  mediaBackground: PropTypes.string,
  postCards: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element)
  ]),
  preCards: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element)
  ]),
  shadow: PropTypes.oneOf(['none', 'light']),
  type: PropTypes.string
};

CardsModule.defaultProps = {
  keyField: 'id'
};

export default CardsModule;
